import { useState, type Ref } from 'react';
import { Box, Button, Menu, MenuItem, Typography } from '@mui/material';
import CalendarTodayOutlined from '@mui/icons-material/CalendarTodayOutlined';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';

export interface BillingPeriodOption {
  /** Billing-period id (the value passed to `onChange`). */
  id: string;
  /** Human label, e.g. "May 2026". */
  label: string;
  /** Marks the currently-open period (badged, and typically not report-eligible). */
  isOpen?: boolean;
}

export interface BillingPeriodSelectorProps {
  periods: BillingPeriodOption[];
  /** Selected period id. */
  value: string | undefined;
  onChange: (id: string) => void;
  /** Prefix label (translated by the caller), e.g. "Billing period:". */
  label?: string;
  /** Badge text for the open period (translated by the caller). Defaults to "Open". */
  openLabel?: string;
  /** Placeholder when nothing is selected (translated by the caller). */
  placeholder?: string;
  disabled?: boolean;
  ref?: Ref<HTMLButtonElement>;
}

/**
 * Billing-period dropdown: a compact outlined trigger showing the selected
 * period, opening a menu of periods with an "Open" badge on the live one. Port
 * of the prototype filter-bar period picker, on MUI `Button` + `Menu` for
 * accessibility. Presentational — the caller supplies the period list (from
 * `GET /api/v1/usage/billing-periods`) and owns the selection.
 */
export function BillingPeriodSelector({
  periods,
  value,
  onChange,
  label,
  openLabel = 'Open',
  placeholder,
  disabled,
  ref,
}: BillingPeriodSelectorProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const selected = periods.find((p) => p.id === value);
  const triggerText = selected?.label ?? placeholder ?? '—';

  return (
    <>
      <Button
        ref={ref}
        variant="outlined"
        color="inherit"
        size="small"
        disabled={disabled}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        startIcon={<CalendarTodayOutlined sx={{ fontSize: 16, color: 'text.secondary' }} />}
        endIcon={<KeyboardArrowDown sx={{ fontSize: 18, color: 'text.secondary' }} />}
        sx={{ textTransform: 'none', fontWeight: 500, borderColor: 'divider' }}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {label && (
          <Box component="span" sx={{ color: 'text.secondary', mr: 0.5, fontWeight: 500 }}>
            {label}
          </Box>
        )}
        <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>
          {triggerText}
        </Box>
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        slotProps={{ paper: { sx: { minWidth: 240, mt: 0.5 } } }}
      >
        {periods.map((p) => (
          <MenuItem
            key={p.id}
            selected={p.id === value}
            onClick={() => {
              onChange(p.id);
              setAnchorEl(null);
            }}
            sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5 }}
          >
            <span>{p.label}</span>
            {p.isOpen && (
              <Typography
                component="span"
                sx={{
                  fontSize: 10,
                  fontWeight: 600,
                  letterSpacing: '0.5px',
                  textTransform: 'uppercase',
                  color: 'secondary.main',
                }}
              >
                {openLabel}
              </Typography>
            )}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
